import { z } from 'zod'

// ─── batch-63 facturen-bronregel (FO-470) ────────────────────────────────────
// Een factuurrij ontstaat nog uitsluitend uit (1) de mailbox verkoopfactuur@,
// (2) handmatige upload/invoer en (3) de eigen facturatie. Wat richting Finqle
// gaat is een facturatieverzoek (`FinqleBillingRequest`) en staat alleen daar;
// de definitieve factuur koppelt aan het verzoek in plaats van een tweede rij te
// vormen. Gedeeld door de API (koppelaar, verzoekenlijst, facturenlijst,
// import-dedupe) en de backoffice.

// ─── Herkomst van een facturatieverzoek ─────────────────────────────────────

/** Spiegel van de Prisma-enum `FinqleVerzoekBron`. */
export const FINQLE_VERZOEK_BRONNEN = ['FINQLE_API', 'STREEP_VERZENDING'] as const
export type FinqleVerzoekBronKey = (typeof FINQLE_VERZOEK_BRONNEN)[number]

export const FINQLE_VERZOEK_BRON_LABELS: Record<FinqleVerzoekBronKey, string> = {
  FINQLE_API: 'Finqle-API',
  STREEP_VERZENDING: 'Verzonden door STREEP',
}

/** Uitleg per herkomst (tooltip op de lijst en het detail). */
export const FINQLE_VERZOEK_BRON_UITLEG: Record<FinqleVerzoekBronKey, string> = {
  FINQLE_API: 'Finqle heeft dit verzoek opgemaakt; STREEP spiegelt de stand via de Finqle-API.',
  STREEP_VERZENDING:
    'Geregistreerd bij het versturen van de werkbon naar Finqle. Zodra Finqle het verzoek via de API meldt, wordt het daaraan gekoppeld.',
}

/**
 * Statussen waarbij het verzoek de deur uit is en er een definitieve factuur
 * verwacht wordt. DRAFT is nog niet ingediend; DISAPPROVED wacht op correctie.
 */
export const FINQLE_VERZOEK_FACTUUR_VERWACHT_STATUSSEN = ['SUBMITTED', 'APPROVED', 'INVOICED'] as const

/** Stand van de definitieve factuur bij een verzoek. */
export type FinqleVerzoekFactuurStand = 'ONTVANGEN' | 'NOG_NIET_ONTVANGEN' | 'NIET_VERWACHT'

export const FINQLE_VERZOEK_FACTUUR_STAND_LABELS: Record<FinqleVerzoekFactuurStand, string> = {
  ONTVANGEN: 'Factuur ontvangen',
  NOG_NIET_ONTVANGEN: 'Factuur nog niet ontvangen',
  NIET_VERWACHT: 'Nog geen factuur verwacht',
}

/**
 * Werkvoorraad-filter op de verzoekenlijst: `zonderFactuur` = verzonden
 * (SUBMITTED/APPROVED/INVOICED) maar de definitieve factuur is nog niet binnen.
 */
export const FINQLE_VERZOEK_WERKVOORRADEN = ['zonderFactuur'] as const
export type FinqleVerzoekWerkvoorraad = (typeof FINQLE_VERZOEK_WERKVOORRADEN)[number]

export const FINQLE_VERZOEK_WERKVOORRAAD_LABELS: Record<FinqleVerzoekWerkvoorraad, string> = {
  zonderFactuur: 'Verzonden, factuur nog niet binnen',
}

/** Leesbare telling voor de teller in de facturenlijst. */
export function verzoekenZonderFactuurTekst(aantal: number): string {
  return aantal === 1
    ? '1 facturatieverzoek zonder ontvangen factuur'
    : `${aantal} facturatieverzoeken zonder ontvangen factuur`
}

// ─── Koppelaar factuur <-> facturatieverzoek ─────────────────────────────────

/** Waarop de koppelaar een binnengekomen factuur aan een verzoek hing. */
export const FACTUUR_VERZOEK_KOPPEL_WIJZEN = [
  'FINQLE_ID',
  'FINQLE_NUMMER',
  'WERKBON',
  'BEDRAG_DATUM',
] as const
export type FactuurVerzoekKoppelWijze = (typeof FACTUUR_VERZOEK_KOPPEL_WIJZEN)[number]

export const FACTUUR_VERZOEK_KOPPEL_WIJZE_LABELS: Record<FactuurVerzoekKoppelWijze, string> = {
  FINQLE_ID: 'op Finqle-factuur-id',
  FINQLE_NUMMER: 'op Finqle-factuurnummer',
  WERKBON: 'op werkbonnummer',
  BEDRAG_DATUM: 'op bedrag en datum',
}

export type FactuurVerzoekKoppelUitkomst =
  /** Aan één verzoek (of de verzoeken van één Finqle-factuur) gekoppeld. */
  | 'GEKOPPELD'
  /** Geen herkenbaar verzoek: gewone factuurrij, markering "zonder facturatieverzoek". */
  | 'GEEN_VERZOEK'
  /** Meerdere kandidaten zonder eenduidige keuze: niets gekoppeld (review). */
  | 'MEERDUIDIG'
  /** Niet van toepassing: creditnota (nooit tegen het origineel ontdubbeld). */
  | 'NIET_VAN_TOEPASSING'

export interface FactuurVerzoekKoppelingDTO {
  uitkomst: FactuurVerzoekKoppelUitkomst
  wijze: FactuurVerzoekKoppelWijze | null
  verzoekIds: string[]
  /** Eén leesbare zin voor toast/review. */
  melding: string
}

export const FACTUUR_ZONDER_VERZOEK_LABEL = 'Zonder facturatieverzoek'

// ─── Facturenlijst: nummer-stand ────────────────────────────────────────────

/**
 * Nummer-stand van een factuurrij. Een concept zonder fiscaal nummer is
 * expliciet een concept (met CF-nummer); een rij zonder enig nummer is een
 * zichtbare uitzondering, nooit een anonieme rij.
 */
export const FACTUUR_NUMMER_STANDEN = ['NUMMER', 'CONCEPT', 'ZONDER_NUMMER'] as const
export type FactuurNummerStand = (typeof FACTUUR_NUMMER_STANDEN)[number]

export const FACTUUR_NUMMER_STAND_LABELS: Record<FactuurNummerStand, string> = {
  NUMMER: 'Met factuurnummer',
  CONCEPT: 'Concept (nog geen factuurnummer)',
  ZONDER_NUMMER: 'Zonder nummer',
}

/** Label in de nummerkolom voor een rij zonder conceptnummer én zonder factuurnummer. */
export const FACTUUR_ZONDER_NUMMER_LABEL = 'Geen nummer'

/** Leid de nummer-stand af (puur; lijst + detail + filter gebruiken dezelfde regel). */
export function bepaalFactuurNummerStand(f: {
  invoiceNumber: string | null
  conceptNumber: string | null
}): FactuurNummerStand {
  if (f.invoiceNumber && f.invoiceNumber.trim() !== '') return 'NUMMER'
  if (f.conceptNumber && f.conceptNumber.trim() !== '') return 'CONCEPT'
  return 'ZONDER_NUMMER'
}

/** Query-waarde van het nummer-filter op `GET /invoices` (leeg = alle). */
export const factuurNummerFilterSchema = z.enum(FACTUUR_NUMMER_STANDEN)

// ─── Dedupe bij binnenkomst (mailbox + upload) ──────────────────────────────

export const FACTUUR_DUPLICAAT_REDENEN = [
  /** Zelfde factuurnummer bij dezelfde BV (levende factuur). */
  'NUMMER_BV',
  /** Zelfde nummer staat nog op een verwijderde factuur van deze BV. */
  'NUMMER_BV_VERWIJDERD',
  /** Hetzelfde bestand (checksum) is al als factuur geboekt. */
  'BESTAND',
] as const
export type FactuurDuplicaatReden = (typeof FACTUUR_DUPLICAAT_REDENEN)[number]

export interface FactuurDuplicaatDTO {
  reden: FactuurDuplicaatReden
  bestaandeFactuur: {
    id: string
    invoiceNumber: string | null
    verwijderd: boolean
  }
  /** Leesbare zin (inline bij het nummerveld of als samenvatting). */
  melding: string
}

/** De meldingstekst per duplicaat-soort (één bron voor API en UI). */
export function factuurDuplicaatMelding(
  reden: FactuurDuplicaatReden,
  nummer: string,
  bestaandNummer: string | null,
): string {
  switch (reden) {
    case 'NUMMER_BV':
      return `Factuurnummer ${nummer} bestaat al bij deze BV. Open de bestaande factuur in plaats van een tweede aan te maken.`
    case 'NUMMER_BV_VERWIJDERD':
      return `Factuurnummer ${nummer} hoort bij een verwijderde factuur van deze BV. Herstel die factuur via het filter "Verwijderde facturen" of controleer het nummer.`
    case 'BESTAND':
      return `Dit bestand is al geboekt als factuur ${bestaandNummer ?? '(zonder nummer)'}. Een tweede aanlevering wordt niet opnieuw geboekt.`
  }
}
